import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, Check, FileText, Loader2, Mail, Phone, Clock, Timer, Hash } from "lucide-react";
import { voicemailApi, type VoicemailDto, type TranscriptDto } from "../api/voicemailApi";
import { HttpError } from "../api/http";
import { WaveformPlayer } from "../components/voicemail/WaveformPlayer";
import "../styles/voicemail.css";

/**
 * Public voicemail page behind the /vm/<guid> link in the GlobalXess
 * voicemail mail. No login: the guid is the credential. Shows who called
 * which number when, a waveform player (pause, scrub, ±10 s, speed) and a
 * Deepgram transcription on request.
 */

const LANGUAGES: { value: string; label: string }[] = [
  { value: "auto", label: "Detect language" },
  { value: "en", label: "English" },
  { value: "nl", label: "Dutch" },
  { value: "de", label: "German" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
];

/** The upload from the switch can lag the mail by a bit; keep checking for a while. */
const AUDIO_RETRY_MS = 5000;
const AUDIO_RETRY_MAX = 36; // 3 minutes

function formatLength(ms: number | null | undefined, seconds?: number | null): string {
  const total = seconds != null && seconds > 0 ? seconds : ms != null ? ms / 1000 : null;
  if (total == null) return "–";
  const whole = Math.round(total);
  return `${Math.floor(whole / 60)}:${(whole % 60).toString().padStart(2, "0")}`;
}

/** "Received" in the customer's time zone (CustomerSetting 'TimeZone', the same one every portal report uses). */
function formatReceived(iso: string, timeZoneIana: string, timeZoneLabel: string): string {
  const d = new Date(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  let text: string;
  try {
    text = new Intl.DateTimeFormat("en-GB", { dateStyle: "full", timeStyle: "short", timeZone: timeZoneIana || "UTC" }).format(d);
  } catch {
    text = new Intl.DateTimeFormat("en-GB", { dateStyle: "full", timeStyle: "short", timeZone: "UTC" }).format(d);
  }
  return `${text} (${timeZoneLabel || "UTC"})`;
}

function prettyNumber(n: string): string {
  if (!n) return "Unknown";
  const l = n.toLowerCase();
  if (l === "anonymous" || l === "restricted" || l === "unavailable" || l === "unknown") return "Withheld number";
  return /^\d{7,}$/.test(n) ? `+${n}` : n;
}

function languageLabel(code: string | null): string | null {
  if (!code) return null;
  const primary = code.split("-")[0].toLowerCase();
  return LANGUAGES.find((l) => l.value === primary)?.label ?? code;
}

export function VoicemailPage() {
  const { key = "" } = useParams<{ key: string }>();
  const [vm, setVm] = useState<VoicemailDto | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [audioAttempt, setAudioAttempt] = useState(0);
  const [audioPending, setAudioPending] = useState(false);
  const [audioSeconds, setAudioSeconds] = useState<number | null>(null);
  const retryTimer = useRef<number | null>(null);

  const [language, setLanguage] = useState("auto");
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);

  // The brand bootstrap sets the tab title once the brand has loaded, so
  // set ours again after every load step.
  useEffect(() => {
    document.title = "Voicemail | GlobalXess";
  }, [loadState]);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    voicemailApi
      .get(key)
      .then((dto) => {
        if (cancelled) return;
        setVm(dto);
        setLoadState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadState(err instanceof HttpError && err.status === 404 ? "notfound" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(
    () => () => {
      if (retryTimer.current != null) window.clearTimeout(retryTimer.current);
    },
    [],
  );

  // A retry marker in the query string makes the player fetch again while the upload is pending.
  const audioSrc = useMemo(() => (vm ? `${voicemailApi.audioUrl(vm.key)}${audioAttempt ? `?_r=${audioAttempt}` : ""}` : ""), [vm, audioAttempt]);

  const onAudioError = useCallback(
    (status: number | null) => {
      if (status === 404) {
        setAudioPending(true);
        setAudioAttempt((n) => {
          if (n >= AUDIO_RETRY_MAX) return n;
          retryTimer.current = window.setTimeout(() => setAudioAttempt((m) => m + 1), AUDIO_RETRY_MS);
          return n;
        });
      } else {
        setAudioPending(false);
      }
    },
    [],
  );

  const onAudioReady = useCallback((seconds: number) => {
    setAudioPending(false);
    setAudioSeconds(seconds);
  }, []);

  const transcribe = async (force: boolean) => {
    if (!vm) return;
    setTranscribing(true);
    setTranscribeError(null);
    try {
      const res: TranscriptDto = await voicemailApi.transcribe(vm.key, language === "auto" ? null : language, force);
      setVm({ ...vm, transcript: res.transcript, transcriptLanguage: res.language, transcribedUtc: res.transcribedUtc });
    } catch (err: unknown) {
      if (err instanceof HttpError) {
        if (err.status === 503) setTranscribeError("Transcription is not available right now.");
        else if (err.status === 404) setTranscribeError("The recording is not available yet. Try again in a moment.");
        else if (err.status === 429) setTranscribeError("Too many requests. Please wait a minute and try again.");
        else setTranscribeError("The transcription failed. Please try again.");
      } else {
        setTranscribeError("The transcription failed. Please try again.");
      }
    } finally {
      setTranscribing(false);
    }
  };

  const received = vm ? formatReceived(vm.receivedUtc, vm.timeZoneIana, vm.timeZone) : null;

  return (
    <div className="vm-page">
      <header className="vm-header">
        <div className="vm-container vm-header-inner">
          <a href="https://globalxess.com" className="vm-logo" aria-label="GlobalXess">
            <img src="/brand/globalxess-logo.svg" alt="GlobalXess" />
          </a>
          <span className="vm-header-tag">Voicemail</span>
        </div>
      </header>

      <main className="vm-main">
        <div className="vm-container">
          {loadState === "loading" && (
            <div className="vm-card vm-center">
              <Loader2 className="vm-spin" size={28} />
              <p>Loading voicemail…</p>
            </div>
          )}

          {loadState === "notfound" && (
            <div className="vm-card vm-center">
              <AlertTriangle size={32} className="vm-warn" />
              <h1>This voicemail link is not valid</h1>
              <p>The link may be incomplete or the message has been removed. Please open the link from the e-mail again.</p>
            </div>
          )}

          {loadState === "error" && (
            <div className="vm-card vm-center">
              <AlertTriangle size={32} className="vm-warn" />
              <h1>Something went wrong</h1>
              <p>The voicemail could not be loaded. Please try again in a moment.</p>
            </div>
          )}

          {loadState === "ready" && vm && received && (
            <>
              <section className="vm-card vm-hero">
                <div className="vm-eyebrow">New voicemail</div>
                <h1 className="vm-title">{vm.calledName || vm.calledNumber}</h1>
                <p className="vm-lead">
                  A caller left a message on <strong>{prettyNumber(vm.calledNumber)}</strong>.
                </p>
                <dl className="vm-meta">
                  <div>
                    <dt><Phone size={15} /> From</dt>
                    <dd className="strong">{prettyNumber(vm.callingNumber)}</dd>
                  </div>
                  <div>
                    <dt><Hash size={15} /> Called number</dt>
                    <dd>{prettyNumber(vm.calledNumber)}</dd>
                  </div>
                  <div>
                    <dt><Clock size={15} /> Received</dt>
                    <dd>{received}</dd>
                  </div>
                  <div>
                    <dt><Timer size={15} /> Length</dt>
                    <dd>{formatLength(vm.lengthMs, audioSeconds)}</dd>
                  </div>
                </dl>
              </section>

              <section className="vm-card">
                <h2 className="vm-h2">Message</h2>
                {audioPending && (
                  <div className="vm-notice">
                    <Loader2 className="vm-spin" size={16} />
                    The recording is still being uploaded from the phone system. This page checks again every few seconds.
                  </div>
                )}
                <WaveformPlayer
                  src={audioSrc}
                  pending={audioPending}
                  downloadHref={voicemailApi.audioUrl(vm.key, true)}
                  downloadName={vm.filename}
                  onLoadError={onAudioError}
                  onReady={onAudioReady}
                />
              </section>

              <section className="vm-card">
                <div className="vm-transcript-head">
                  <h2 className="vm-h2"><FileText size={18} /> Transcript</h2>
                  {vm.transcript && (
                    <span className="vm-chip">
                      <Check size={13} /> {languageLabel(vm.transcriptLanguage) ?? "Transcribed"}
                    </span>
                  )}
                </div>

                {vm.transcript ? (
                  <>
                    <blockquote className="vm-transcript">{vm.transcript}</blockquote>
                    <div className="vm-transcript-actions">
                      <select className="vm-select" value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Language">
                        {LANGUAGES.map((l) => (
                          <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                      </select>
                      <button type="button" className="vm-btn vm-btn-secondary" onClick={() => transcribe(true)} disabled={transcribing}>
                        {transcribing ? <Loader2 className="vm-spin" size={16} /> : <FileText size={16} />}
                        {transcribing ? "Transcribing…" : "Transcribe again"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="vm-muted">Turn the message into text. Detection works well for most languages; pick one if you already know it.</p>
                    <div className="vm-transcript-actions">
                      <select className="vm-select" value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Language">
                        {LANGUAGES.map((l) => (
                          <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                      </select>
                      <button type="button" className="vm-btn vm-btn-primary" onClick={() => transcribe(false)} disabled={transcribing || audioPending}>
                        {transcribing ? <Loader2 className="vm-spin" size={16} /> : <FileText size={16} />}
                        {transcribing ? "Transcribing…" : "Transcribe"}
                      </button>
                    </div>
                  </>
                )}
                {transcribeError && (
                  <div className="vm-error">
                    <AlertTriangle size={15} /> {transcribeError}
                  </div>
                )}
              </section>

              <p className="vm-footnote">
                <Mail size={14} />
                <span>
                  This message was also sent as an e-mail attachment and is stored as a call recording in the portal.
                </span>
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="vm-footer">
        <div className="vm-container vm-footer-grid">
          <div>
            <div className="vm-footer-brand">GlobalXess</div>
          </div>
          <div className="vm-footer-links">
            <a href="https://globalxess.com">globalxess.com</a>
          </div>
        </div>
        <div className="vm-container vm-footer-legal">
          <span>© {new Date().getFullYear()} GlobalXess · Operated by Global Premium Telecom B.V. · KvK 71556966 · VAT NL858761567B01</span>
          <span>
            <a href="https://globalxess.com/privacy">Privacy</a> · <a href="https://globalxess.com/terms">Terms</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
